<template>
  <Dialog :open="open" @update:open="(value) => emit('update:open', value)">
    <DialogScrollContent size="md" @interactOutside.prevent>
      <DialogHeader>
        <DialogTitle>{{ workflow ? $t('Edit the workflow') : $t('Create a workflow') }}</DialogTitle>
        <DialogDescription>
          {{ $t('A workflow gathers the automatic messages of a use case, such as the life of a booking. The messages keep their own trigger, targeting and sending.') }}
        </DialogDescription>
      </DialogHeader>

      <form id="marketing-workflow-form" @submit.prevent="save">
        <div v-if="!workflow" class="tw:mb-4 tw:grid tw:grid-cols-2 tw:gap-2.5" role="radiogroup" :aria-label="$t('Start from')">
          <button v-for="choice in startChoices" :key="choice.value" type="button" role="radio"
                  :aria-checked="mode === choice.value"
                  class="tw:rounded-xl tw:border tw:bg-white tw:px-3.5 tw:py-3 tw:text-left"
                  :class="mode === choice.value ? 'tw:border-2 tw:border-blue-600' : 'tw:border-zinc-300 tw:hover:border-zinc-500'"
                  @click="mode = choice.value">
            <span class="tw:flex tw:items-center tw:gap-2 tw:text-sm tw:font-medium">
              <component :is="choice.icon" class="tw:size-4" aria-hidden="true" /> {{ choice.label }}
            </span>
            <span class="tw:mt-0.5 tw:block tw:text-xs tw:text-zinc-500">{{ choice.help }}</span>
          </button>
        </div>

        <fieldset v-if="mode === 'template'" class="form-group">
          <legend class="tw:text-base">{{ $t('Template') }}</legend>
          <div class="tw:divide-y tw:divide-zinc-200 tw:rounded-lg tw:border tw:border-zinc-200">
            <label v-for="template in templates" :key="template.key"
                   class="tw:m-0 tw:flex tw:items-start tw:gap-3 tw:px-3 tw:py-2.5"
                   :class="template.available ? 'tw:cursor-pointer' : 'tw:text-zinc-400'">
              <input v-model="form.template" type="radio" name="template" class="tw:mt-1" :value="template.key"
                     :disabled="!template.available" @change="nameAfter(template)">
              <span class="tw:flex-1">
                <span class="tw:block tw:text-sm tw:font-medium">{{ template.name }}</span>
                <span class="tw:block tw:text-xs" :class="template.available ? 'tw:text-zinc-500' : ''">
                  {{ template.available ? template.description : template.requirement }}
                </span>
                <span v-if="template.available && form.template === template.key"
                      class="tw:mt-1.5 tw:flex tw:flex-wrap tw:gap-1">
                  <span v-for="step in template.steps" :key="step.name"
                        class="tw:rounded-full tw:bg-zinc-100 tw:px-2 tw:py-0.5 tw:text-xs tw:text-zinc-700">
                    {{ step.name }}
                  </span>
                </span>
              </span>
              <span class="tw:text-xs tw:text-zinc-500">{{ $t('{n} message | {n} messages', template.steps.length) }}</span>
            </label>
          </div>
          <div v-if="errors.template" class="invalid-feedback d-block">{{ errors.template[0] }}</div>
          <p class="tw:mt-2 tw:text-xs tw:text-zinc-500">
            {{ $t('Its messages are created as drafts, for you to write and turn on.') }}
          </p>
        </fieldset>

        <div class="form-group">
          <label for="marketing-workflow-name">{{ $t('Name') }}</label>
          <input id="marketing-workflow-name" v-model="form.name" type="text" class="form-control"
                 :class="{ 'is-invalid': errors.name }" :placeholder="$t('Booking management')" required maxlength="255">
          <div v-if="errors.name" class="invalid-feedback">{{ errors.name[0] }}</div>
        </div>

        <div class="form-group">
          <label for="marketing-workflow-description">
            {{ $t('Description') }} <span class="tw:text-zinc-500">({{ $t('optional') }})</span>
          </label>
          <textarea id="marketing-workflow-description" v-model="form.description" rows="2" class="form-control"
                    :class="{ 'is-invalid': errors.description }" maxlength="1000"></textarea>
          <div v-if="errors.description" class="invalid-feedback">{{ errors.description[0] }}</div>
        </div>

        <fieldset v-if="mode === 'blank'" class="form-group">
          <legend class="tw:text-base">{{ $t('Automatic messages') }}</legend>
          <p v-if="automaticMessages.length === 0" class="tw:text-sm tw:text-zinc-500">
            {{ $t('The event has no automatic message yet. Create one and choose this workflow in its properties.') }}
          </p>
          <div v-else class="tw:divide-y tw:divide-zinc-200 tw:rounded-lg tw:border tw:border-zinc-200">
            <label v-for="message in automaticMessages" :key="message.id"
                   class="tw:m-0 tw:flex tw:cursor-pointer tw:items-center tw:gap-3 tw:px-3 tw:py-2">
              <input v-model="form.marketing_message_ids" type="checkbox" :value="message.id">
              <span class="tw:flex-1">
                <span class="tw:block tw:text-sm tw:font-medium">{{ message.name }}</span>
                <span class="tw:text-xs tw:text-zinc-500">{{ message.event }}</span>
              </span>
              <span v-if="otherWorkflowName(message)" class="tw:text-xs tw:text-zinc-500">
                {{ $t('In {name}', { name: otherWorkflowName(message) }) }}
              </span>
            </label>
          </div>
          <div v-if="messageError" class="invalid-feedback d-block">{{ messageError }}</div>
          <p class="tw:mt-2 tw:text-xs tw:text-zinc-500">
            {{ $t('A message belongs to one workflow at most: picking one from another workflow moves it here.') }}
          </p>
        </fieldset>
      </form>

      <Alert v-if="failed" variant="danger">{{ $t('The workflow could not be saved. Try again.') }}</Alert>

      <DialogFooter>
        <button type="button" class="btn btn-light" @click="emit('update:open', false)">{{ $t('Cancel') }}</button>
        <button type="submit" form="marketing-workflow-form" class="btn btn-primary" :disabled="saving">
          {{ workflow ? $t('Save') : (mode === 'template' ? $t('Install the workflow') : $t('Create the workflow')) }}
        </button>
      </DialogFooter>
    </DialogScrollContent>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import axios from 'axios'
import { LayoutTemplate, PencilLine } from '@lucide/vue'
import { Dialog, DialogDescription, DialogFooter, DialogHeader, DialogScrollContent, DialogTitle } from '@/components/ui/dialog'
import Alert from '@/components/ui/Alert.vue'
import type { AutomaticMessageChoice, BoardWorkflow, WorkflowTemplateChoice } from './types'

const props = withDefaults(defineProps<{
  open: boolean;
  /** The workflow to edit, none to create one. */
  workflow: BoardWorkflow | null;
  workflows: BoardWorkflow[];
  automaticMessages: AutomaticMessageChoice[];
  templates?: WorkflowTemplateChoice[];
  storeUrl: string;
  updateUrl: string;
}>(), {
  templates: () => [],
})

const emit = defineEmits<{
  (event: 'update:open', value: boolean): void;
  (event: 'saved', id: number): void;
}>()

const { t } = useI18n()

/** A new workflow starts from a template, copied with draft messages, or blank with messages picked. */
const mode = ref<'template' | 'blank'>('blank')

const startChoices = computed(() => [
  { value: 'template' as const, icon: LayoutTemplate, label: t('From a template'), help: t('Draft messages to adjust') },
  { value: 'blank' as const, icon: PencilLine, label: t('Blank'), help: t('Pick existing messages') },
])

const form = ref({ name: '', description: '', template: null as string | null, marketing_message_ids: [] as number[] })
const errors = ref<Record<string, string[]>>({})
const saving = ref(false)
const failed = ref(false)

const firstAvailableTemplate = (): WorkflowTemplateChoice | undefined => props.templates.find((template) => template.available)

watch(() => props.open, (open) => {
  if (!open) return
  const template = props.workflow ? undefined : firstAvailableTemplate()
  mode.value = template ? 'template' : 'blank'
  form.value = {
    name: props.workflow?.name ?? template?.name ?? '',
    description: props.workflow?.description ?? '',
    template: template?.key ?? null,
    marketing_message_ids: [...(props.workflow?.message_ids ?? [])],
  }
  errors.value = {}
  failed.value = false
})

/** Names the workflow after the template picked, unless the team already named it otherwise. */
const nameAfter = (template: WorkflowTemplateChoice): void => {
  const untouched = form.value.name === '' || props.templates.some((other) => other.name === form.value.name)
  if (untouched) form.value.name = template.name
}

const payload = computed(() => mode.value === 'template'
  ? { name: form.value.name, description: form.value.description, template: form.value.template }
  : { name: form.value.name, description: form.value.description, marketing_message_ids: form.value.marketing_message_ids })

const messageError = computed(() => Object.entries(errors.value)
  .find(([field]) => field.startsWith('marketing_message_ids'))?.[1][0])

const otherWorkflowName = (message: AutomaticMessageChoice): string | undefined => {
  if (message.marketing_workflow_id === null || message.marketing_workflow_id === props.workflow?.id) return undefined

  return props.workflows.find((workflow) => workflow.id === message.marketing_workflow_id)?.name
}

const save = async (): Promise<void> => {
  saving.value = true
  errors.value = {}
  failed.value = false
  try {
    const { data } = props.workflow
      ? await axios.put(props.updateUrl.replace('__workflow__', String(props.workflow.id)), payload.value)
      : await axios.post(props.storeUrl, payload.value)
    emit('saved', data.id)
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 422) {
      errors.value = error.response.data.errors ?? error.response.data.message ?? {}
    } else {
      failed.value = true
    }
  } finally {
    saving.value = false
  }
}
</script>
